import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, scrollToSection } from "../constants/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1730514785075-b065c757b653?crop=entropy&cs=srgb&fm=jpg&q=85";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Counter = ({ target }) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        const controls = animate(0, target, {
            duration: 2.2,
            delay: 1.4,
            ease: "easeOut",
            onUpdate: (v) => setValue(Math.round(v)),
        });
        return () => controls.stop();
    }, [target]);
    return <span>{value.toLocaleString("en-CA")}</span>;
};

export const Hero = ({ city }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const cityName = city?.name;

    return (
        <section
            ref={ref}
            id="top"
            className="relative min-h-[100svh] flex items-center overflow-hidden noise-overlay"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt={
                        cityName
                            ? `Flatbed tow truck picking up a scrap car in ${cityName}`
                            : "Flatbed tow truck hauling a scrap car in Toronto"
                    }
                    className="h-[120%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/55 to-white/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/40" />
            </motion.div>

            <motion.div
                style={{ opacity: contentOpacity }}
                className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full"
            >
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="flex items-center gap-3 mb-7"
                >
                    <span className="h-2 w-2 rounded-full bg-[#FF3B30] animate-blink" />
                    <span className="font-mono-ind text-xs uppercase tracking-[0.32em] text-[#FF3B30]">
                        {cityName
                            ? `Now serving ${cityName} & the GTA`
                            : "Toronto's #1 Scrap Car Buyer"}
                    </span>
                </motion.div>

                <h1 className="font-display font-black uppercase leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#141721] max-w-4xl">
                    <MaskedLine delay={0.3}>Cash for cars</MaskedLine>
                    {cityName ? (
                        <>
                            <MaskedLine delay={0.45}>
                                <span className="text-stroke-white">{`in ${cityName}`}</span>
                            </MaskedLine>
                            <MaskedLine delay={0.6}>
                                <span className="text-[#FF3B30]">same-day pickup.</span>
                            </MaskedLine>
                        </>
                    ) : (
                        <MaskedLine delay={0.45}>
                            <span className="text-[#FF3B30]">Toronto</span>
                        </MaskedLine>
                    )}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85 }}
                    className="mt-7 max-w-xl text-base sm:text-lg text-[#565B6B] leading-relaxed"
                >
                    {cityName ? (
                        <>
                            We buy junk, scrap and unwanted vehicles in {cityName}. {city.blurb}{" "}
                            <span className="text-[#141721] font-semibold">Cash on the spot</span> —
                            free flatbed towing included.
                        </>
                    ) : (
                        <>
                            Get paid up to{" "}
                            <span className="text-[#141721] font-semibold">
                                $10,000 cash on the spot
                            </span>{" "}
                            for your junk, broken or unwanted vehicle. Free same-day flatbed
                            towing anywhere in Toronto &amp; the Greater Toronto Area.
                        </>
                    )}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1 }}
                    className="mt-10 flex flex-wrap items-center gap-5"
                >
                    <a
                        href={PHONE_TEL}
                        data-testid="hero-call-cta"
                        className="animate-pulse-ring inline-flex items-center gap-3 rounded-md bg-[#FF3B30] px-8 py-4 font-display font-extrabold text-lg text-white uppercase tracking-wide transition-all duration-300 hover:bg-[#E0342B] hover:-translate-y-0.5"
                    >
                        <Phone className="h-5 w-5" strokeWidth={2.6} />
                        Call {PHONE_DISPLAY}
                    </a>
                    <button
                        data-testid="hero-how-it-works-link"
                        onClick={() => scrollToSection("how-it-works")}
                        className="group inline-flex items-center gap-2 text-sm font-semibold text-[#565B6B] hover:text-[#141721] transition-colors duration-300"
                    >
                        See how it works
                        <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.3 }}
                    className="mt-16 flex flex-wrap gap-x-12 gap-y-6"
                >
                    {[
                        { value: <Counter target={37} />, suffix: "", label: "Cars bought this week" },
                        { value: <Counter target={18} />, suffix: "+", label: "Years in the GTA" },
                        { value: <Counter target={10000} />, suffix: "", prefix: "$", label: "Top cash paid out" },
                    ].map((stat, i) => (
                        <div key={i} data-testid={`hero-stat-${i}`}>
                            <div className="font-display font-extrabold text-2xl sm:text-3xl text-[#141721]">
                                {stat.prefix}
                                {stat.value}
                                {stat.suffix}
                            </div>
                            <div className="font-mono-ind text-[11px] uppercase tracking-widest text-[#6B7280] mt-1">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            <div className="absolute bottom-0 inset-x-0 h-2 hazard-stripes" />
        </section>
    );
};
